// src/pages/TestPage.tsx
// Combined test runner for Phase 1 and Phase 2

import React, { useState } from 'react';
import { runPhase1Tests } from '../utils/phase1Tests';
import { runPhase2Tests } from '../utils/phase2Tests';

interface TestResult {
  testName: string;
  passed: boolean;
  message: string;
  details?: any;
}

const TestPage: React.FC = () => {
  const [phase1Results, setPhase1Results] = useState<TestResult[] | null>(null);
  const [phase2Results, setPhase2Results] = useState<TestResult[] | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [activeTab, setActiveTab] = useState<'phase1' | 'phase2'>('phase1');

  const handleRunPhase1 = async () => {
    setIsRunning(true);
    setPhase1Results(null);
    
    try {
      console.log('🧪 Running Phase 1 tests...');
      const results = await runPhase1Tests();
      setPhase1Results(results);
      
      const passed = results.filter(r => r.passed).length;
      const total = results.length;
      
      if (passed === total) {
        alert(`✅ Phase 1: All tests passed! (${passed}/${total})`);
      } else {
        alert(`⚠️ Phase 1: ${passed}/${total} tests passed. Check results below.`);
      }
    } catch (error) {
      alert(`❌ Phase 1 tests failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsRunning(false);
    }
  };

  const handleRunPhase2 = async () => {
    setIsRunning(true);
    setPhase2Results(null);
    
    try {
      console.log('🧪 Running Phase 2 tests...');
      const results = await runPhase2Tests();
      setPhase2Results(results);
      
      const passed = results.filter(r => r.passed).length;
      const total = results.length;
      
      if (passed === total) {
        alert(`✅ Phase 2: All tests passed! (${passed}/${total})`);
      } else {
        alert(`⚠️ Phase 2: ${passed}/${total} tests passed. Check results below.`);
      }
    } catch (error) {
      alert(`❌ Phase 2 tests failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsRunning(false);
    }
  };

  const handleRunAll = async () => {
    setIsRunning(true);
    setPhase1Results(null);
    setPhase2Results(null);
    
    try {
      console.log('🧪 Running all tests...');
      
      const p1Results = await runPhase1Tests();
      setPhase1Results(p1Results);
      
      const p2Results = await runPhase2Tests();
      setPhase2Results(p2Results);
      
      const p1Passed = p1Results.filter(r => r.passed).length;
      const p1Total = p1Results.length;
      const p2Passed = p2Results.filter(r => r.passed).length;
      const p2Total = p2Results.length;
      const totalPassed = p1Passed + p2Passed;
      const totalTests = p1Total + p2Total;
      
      if (totalPassed === totalTests) {
        alert(
          `✅ All tests passed! (${totalPassed}/${totalTests})\n\n` +
          `Phase 1: ${p1Passed}/${p1Total}\n` +
          `Phase 2: ${p2Passed}/${p2Total}`
        );
      } else {
        alert(
          `⚠️ ${totalPassed}/${totalTests} tests passed\n\n` +
          `Phase 1: ${p1Passed}/${p1Total}\n` +
          `Phase 2: ${p2Passed}/${p2Total}\n\n` +
          'Check results below for details.'
        );
      }
    } catch (error) {
      alert(`❌ Tests failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsRunning(false);
    }
  };

  const renderResults = (results: TestResult[] | null, phase: string) => {
    if (!results) return null;
    
    const passed = results.filter(r => r.passed).length;
    const failed = results.filter(r => !r.passed).length;
    
    return (
      <div style={{
        backgroundColor: 'white',
        border: '2px solid #dee2e6',
        borderRadius: '12px',
        padding: '25px',
        marginTop: '20px'
      }}>
        <h2 style={{ margin: '0 0 20px 0', color: '#212529' }}>
          📊 {phase} Test Results
        </h2>
        
        <div style={{
          display: 'flex',
          gap: '20px',
          marginBottom: '20px'
        }}>
          <div style={{ 
            padding: '10px 20px',
            backgroundColor: '#d4edda',
            border: '1px solid #c3e6cb',
            borderRadius: '8px',
            fontWeight: 'bold',
            color: '#155724'
          }}>
            ✅ Passed: {passed}
          </div>
          <div style={{ 
            padding: '10px 20px',
            backgroundColor: failed > 0 ? '#f8d7da' : '#e9ecef',
            border: `1px solid ${failed > 0 ? '#f5c6cb' : '#dee2e6'}`,
            borderRadius: '8px',
            fontWeight: 'bold',
            color: failed > 0 ? '#721c24' : '#6c757d'
          }}>
            ❌ Failed: {failed}
          </div>
        </div>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {results.map((result, index) => (
            <div 
              key={index}
              style={{
                backgroundColor: '#f8f9fa',
                padding: '15px',
                borderRadius: '8px',
                borderLeft: `4px solid ${result.passed ? '#28a745' : '#dc3545'}`
              }}
            >
              <div style={{ 
                fontWeight: 'bold',
                marginBottom: '8px',
                fontSize: '15px'
              }}>
                {result.passed ? '✅' : '❌'} {result.testName}
              </div>
              <div style={{ fontSize: '14px', color: '#495057', marginBottom: '8px' }}>
                {result.message}
              </div>
              {result.details && (
                <details style={{ 
                  marginTop: '8px',
                  fontSize: '13px',
                  color: '#6c757d'
                }}>
                  <summary style={{ 
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    padding: '4px 0'
                  }}>
                    📋 View Details
                  </summary>
                  <pre style={{ 
                    marginTop: '8px',
                    padding: '12px',
                    backgroundColor: 'white',
                    borderRadius: '4px',
                    overflow: 'auto',
                    fontSize: '12px',
                    border: '1px solid #dee2e6'
                  }}>
                    {JSON.stringify(result.details, null, 2)}
                  </pre>
                </details>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div style={{
      maxWidth: '1000px',
      margin: '40px auto',
      padding: '20px',
      fontFamily: 'system-ui, -apple-system, sans-serif'
    }}>
      {/* Header */}
      <div style={{
        backgroundColor: '#f8f9fa',
        padding: '30px',
        borderRadius: '12px',
        marginBottom: '30px',
        border: '2px solid #dee2e6'
      }}>
        <h1 style={{ margin: '0 0 10px 0', color: '#212529' }}>
          🧪 Sharing Feature Test Suite
        </h1>
        <p style={{ margin: 0, color: '#6c757d', fontSize: '16px' }}>
          Automated tests for Phase 1 (Database Schema) and Phase 2 (Backend Services)
        </p>
      </div>

      {/* Instructions */}
      <div style={{
        backgroundColor: '#d1ecf1',
        border: '1px solid #bee5eb',
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '30px'
      }}>
        <h3 style={{ margin: '0 0 10px 0', color: '#0c5460' }}>
          ℹ️ Before Running Tests
        </h3>
        <ul style={{ margin: '10px 0', paddingLeft: '20px', color: '#0c5460' }}>
          <li><strong>Sign in</strong> to your app first</li>
          <li><strong>Phase 1 tests</strong> verify database schema (users, videos, shares)</li>
          <li><strong>Phase 2 tests</strong> verify sharing logic (limits, tracking, cleanup)</li>
          <li>Tests will <strong>create temporary data</strong> - this is normal</li>
          <li>Check the browser console for detailed logs</li>
        </ul>
      </div>

      {/* Action Buttons */}
      <div style={{
        display: 'flex',
        gap: '15px',
        marginBottom: '30px',
        flexWrap: 'wrap'
      }}>
        <button
          onClick={handleRunPhase1}
          disabled={isRunning}
          style={{
            flex: '1 1 200px',
            padding: '15px 30px',
            backgroundColor: isRunning ? '#6c757d' : '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: 'bold',
            cursor: isRunning ? 'not-allowed' : 'pointer',
            transition: 'background-color 0.2s'
          }}
        >
          {isRunning ? '⏳ Running...' : '🧪 Test Phase 1'}
        </button>

        <button
          onClick={handleRunPhase2}
          disabled={isRunning}
          style={{
            flex: '1 1 200px',
            padding: '15px 30px',
            backgroundColor: isRunning ? '#6c757d' : '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: 'bold',
            cursor: isRunning ? 'not-allowed' : 'pointer',
            transition: 'background-color 0.2s'
          }}
        >
          {isRunning ? '⏳ Running...' : '🧪 Test Phase 2'}
        </button>

        <button
          onClick={handleRunAll}
          disabled={isRunning}
          style={{
            flex: '1 1 200px',
            padding: '15px 30px',
            backgroundColor: isRunning ? '#6c757d' : '#FF6B00',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: 'bold',
            cursor: isRunning ? 'not-allowed' : 'pointer',
            transition: 'background-color 0.2s'
          }}
        >
          {isRunning ? '⏳ Running...' : '🚀 Run All Tests'}
        </button>
      </div>

      {/* Tab Navigation */}
      {(phase1Results || phase2Results) && (
        <div style={{
          display: 'flex',
          gap: '10px',
          marginBottom: '20px',
          borderBottom: '2px solid #dee2e6'
        }}>
          <button
            onClick={() => setActiveTab('phase1')}
            style={{
              padding: '12px 24px',
              backgroundColor: activeTab === 'phase1' ? '#007bff' : 'transparent',
              color: activeTab === 'phase1' ? 'white' : '#495057',
              border: 'none',
              borderBottom: activeTab === 'phase1' ? '3px solid #007bff' : 'none',
              borderRadius: '8px 8px 0 0',
              fontSize: '15px',
              fontWeight: 'bold',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            Phase 1 Results
            {phase1Results && ` (${phase1Results.filter(r => r.passed).length}/${phase1Results.length})`}
          </button>
          
          <button
            onClick={() => setActiveTab('phase2')}
            style={{
              padding: '12px 24px',
              backgroundColor: activeTab === 'phase2' ? '#007bff' : 'transparent',
              color: activeTab === 'phase2' ? 'white' : '#495057',
              border: 'none',
              borderBottom: activeTab === 'phase2' ? '3px solid #007bff' : 'none',
              borderRadius: '8px 8px 0 0',
              fontSize: '15px',
              fontWeight: 'bold',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            Phase 2 Results
            {phase2Results && ` (${phase2Results.filter(r => r.passed).length}/${phase2Results.length})`}
          </button>
        </div>
      )}

      {/* Results Display */}
      {activeTab === 'phase1' && renderResults(phase1Results, 'Phase 1')}
      {activeTab === 'phase2' && renderResults(phase2Results, 'Phase 2')}

      {/* Footer */}
      <div style={{
        marginTop: '40px',
        padding: '20px',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        fontSize: '14px',
        color: '#6c757d'
      }}>
        <strong>💡 What These Tests Check:</strong>
        <div style={{ marginTop: '10px' }}>
          <strong>Phase 1:</strong>
          <ul style={{ margin: '5px 0 15px 20px' }}>
            <li>User schema has sharing fields</li>
            <li>Video schema has sharing fields</li>
            <li>SharedSessions collection exists</li>
            <li>Security rules allow proper access</li>
            <li>Migration completed successfully</li>
          </ul>
          
          <strong>Phase 2:</strong>
          <ul style={{ margin: '5px 0', paddingLeft: '20px' }}>
            <li>Share creation with 30 view / 7 day limits</li>
            <li>Share loading and validation</li>
            <li>View count tracking</li>
            <li>Expiry date validation</li>
            <li>1 active share per user limit</li>
            <li>Helper functions work correctly</li>
            <li>Cleanup functions execute</li>
            <li>User statistics update</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default TestPage;